import axios from 'axios';

const apiURL = process.env.REACT_APP_API_URL;

let currentToken = null;

export const api = axios.create({
  baseURL: apiURL,
});

export const getAccessToken = () => currentToken;

// Set token after login/signup or refresh
export const setAuthToken = (tokenData) => {
  currentToken = tokenData.access_token;
  localStorage.setItem('refresh_token', tokenData.refresh_token);
};

// Refresh access token using refresh token
const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) throw new Error('No refresh token');

  const response = await axios.post(`${apiURL}/auth/refresh`, {
    refresh_token: refreshToken,
  });

  setAuthToken(response.data);
  return response.data.access_token;
};

// Request interceptor: ensures token is attached or refreshed
api.interceptors.request.use(async (config) => {
  if (!currentToken) {
    try {
      currentToken = await refreshAccessToken();
    } catch (err) {
      console.error('Unable to refresh token before request:', err);
    }
  }

  if (currentToken) {
    config.headers.Authorization = `Bearer ${currentToken}`;
  }

  return config;
});

// Response interceptor: retry once on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      localStorage.getItem('refresh_token')
    ) {
      originalRequest._retry = true;
      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest); // retry with new token
      } catch (refreshError) {
        console.error('Refresh failed after 401:', refreshError);
        localStorage.removeItem('refresh_token');
        currentToken = null;
      }
    }

    return Promise.reject(error);
  }
);

export const clearAuth = () => {
  currentToken = null;
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('papaya_user_email');
  delete api.defaults.headers.common.Authorization;
};

// Generic request
export const retryableHttpRequest = async (
  url,
  attemptNumber = 0,
  maxRetries = 3,
  method = 'GET',
  data,
) => {
  try {
    const response = await api({
      method,
      url,
      data,
    });

    return response.data;
  } catch (error) {
    if (attemptNumber >= maxRetries) throw new Error(error);
    setTimeout(() => {
      retryableHttpRequest(url, attemptNumber + 1, maxRetries, method, data);
    }, 500);
  }
};

// Streaming request (now supports token refresh)
export const streamPost = async (url, data) => {
  if (!currentToken) {
    try {
      currentToken = await refreshAccessToken();
    } catch (err) {
      console.error('Unable to refresh before stream:', err);
    }
  }

  const response = await fetch(`${apiURL}${url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${currentToken}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.body) {
    throw new Error('Streaming not supported by the browser or server');
  }

  return response.body.getReader();
};
